// src/hooks/useDynamicScripts.ts
import { useEffect } from 'react';

/**
 * Executes every <script> found inside an HTML string.
 *  • <script src="…"> is loaded only once per src
 *  • Inline <script> runs at each render
 *  • Cleans up external script tags on unmount if you want
 */
const loadedSrcs = new Set<string>();

export function useDynamicScripts(html: string, removeOnUnmount = false) {
  useEffect(() => {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    const externals: HTMLScriptElement[] = [];
    const inlines: HTMLScriptElement[] = [];

    temp.querySelectorAll('script').forEach(s => {
      if (s.src) externals.push(s);
      else       inlines.push(s);
    });

    // 1-a · external scripts
    externals.forEach(old => {
      if (loadedSrcs.has(old.src)) return;           // already loaded

      const fresh = document.createElement('script');
      fresh.src   = old.src;
      fresh.async = true;
      document.head.appendChild(fresh);
      loadedSrcs.add(old.src);

      if (removeOnUnmount) {
        // remember what we added so we can remove later
        fresh.setAttribute('data-dynamic', 'true');
      }
    });

    // 1-b · inline scripts (executed immediately)
    inlines.forEach(old => {
      try {
        // eslint-disable-next-line no-eval
        eval(old.textContent || '');
      } catch (e) {
        console.error('Dynamic inline script error', e);
      }
    });

    return () => {
      if (!removeOnUnmount) return;
      document
        .querySelectorAll('script[data-dynamic="true"]')
        .forEach(s => {
          document.head.removeChild(s);
          loadedSrcs.delete((s as HTMLScriptElement).src);
        });
    };
  }, [html, removeOnUnmount]);
}
